import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as XLSX from "xlsx";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  Lock,
  Maximize2,
  Pencil,
  Pin,
  Save,
  Search,
  Upload,
  WrapText,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useUploadCaptableExcel } from "../../queries/dataPipeline";
import { evaluateGrid } from "./formulaEngine";

interface Props {
  url: string;
  title: string;
  onClose: () => void;
  // When provided, the workbook becomes editable and edits can be uploaded back.
  runId?: string;
  cin?: string;
  // Which generated artifact saves overwrite: the stage-3 result (default) or
  // the generated stage-4 onboarding workbook.
  target?: "stage3" | "stage4";
  onSaved?: () => void;
}

interface SheetData {
  name: string;
  rows: string[][];
}

type CellCoord = { row: number; col: number };

// Height (px) of the sticky column-letter header row — used as the offset for
// the (optional) frozen first data row so it sits flush beneath it.
const HEADER_H = 22;
const DEFAULT_COL_WIDTH = 120;
const MIN_COL_WIDTH = 40;

// Convert 0-based column index to Excel-style letter(s): 0→A, 25→Z, 26→AA …
function colLabel(n: number): string {
  let s = "";
  let i = n;
  do {
    s = String.fromCharCode(65 + (i % 26)) + s;
    i = Math.floor(i / 26) - 1;
  } while (i >= 0);
  return s;
}

// Drop trailing fully-empty rows so the grid crops to the active data range
// instead of rendering hundreds of blank rows from the sheet's declared size.
function trimTrailingEmptyRows(rows: string[][]): string[][] {
  let last = -1;
  rows.forEach((r, i) => {
    if (r.some((c) => String(c ?? "").trim() !== "")) last = i;
  });
  return rows.slice(0, last + 1);
}

// Estimate a comfortable width per column from its longest cell so columns
// auto-fit their content on load instead of clipping into "G…", "Pl…", "D…".
function computeAutoFitWidths(rows: string[][]): number[] {
  const CHAR_PX = 7; // approx width of a Calibri 12px character
  const PADDING = 18;
  const FIT_MIN = 56;
  const FIT_MAX = 420; // cap so one verbose cell can't blow out the layout
  const colCount = rows.reduce((m, r) => Math.max(m, r.length), 0);
  const widths: number[] = [];
  for (let c = 0; c < colCount; c += 1) {
    let maxLen = 1;
    rows.forEach((r) => {
      // Longest single line in the cell drives the unwrapped width.
      const text = String(r[c] ?? "");
      const longestLine = text
        .split(/\r?\n/)
        .reduce((m, line) => Math.max(m, line.length), 0);
      if (longestLine > maxLen) maxLen = longestLine;
    });
    widths[c] = Math.min(
      FIT_MAX,
      Math.max(FIT_MIN, maxLen * CHAR_PX + PADDING),
    );
  }
  return widths;
}

// ─── Enum dropdowns for template columns (edit mode) ─────────────────────────
//
// SheetJS (community) drops native Excel data-validations on read, so the
// dropdowns are driven by header/cell rules matching the stage-3 and stage-4
// captable templates. Values mirror what the downstream loaders accept.

const YES_NO = ["Yes", "No"];
const SECURITY_TYPES = [
  "Equity",
  "CCPS",
  "OCPS",
  "OCRPS",
  "RPS",
  "CCD",
  "OCD",
  "NCD",
  "Note",
  "Warrant",
  "Options",
  "Forfeited",
];
const ISSUANCE_TYPES = [
  "Allotment",
  "Bonus",
  "Partly_Paid",
  "ESOP_Exercise",
  "Anti_Dilution",
];
const CURRENCIES = [
  "INR - ₹",
  "USD - $",
  "AED - د.إ",
  "AUD - A$",
  "CNY - ¥",
  "EUR - €",
  "GBP - £",
  "JPY - ¥",
  "SGD - S$",
];
// Stage-3 loader enums (compact plural forms — see backend stage3/mappings.ts).
const STAGE3_SHAREHOLDER_TYPES = [
  "Founders",
  "Angels",
  "Incubator",
  "Accelerator",
  "AngelNetwork",
  "AngelFund",
  "VC",
  "Corporate",
  "FamilyOffice",
  "PE",
  "FinancialInstitution",
  "Options",
  "Employee",
  "HNI",
  "Ex_Employee",
  "Others",
];
// Stage-4 onboarding parser labels (spaced singular forms — new-equion
// generateCompanyInvestorType matches these exactly).
const STAGE4_SHAREHOLDER_TYPES = [
  "Founder",
  "Angel",
  "Incubator",
  "Accelerator",
  "Angel Network",
  "Angel Fund",
  "VC",
  "Corporate",
  "Family Office",
  "PE",
  "Financial Institution",
  "Options",
  "Employee",
  "HNI",
  "Ex_Employee",
  "Others",
];

interface HeaderEnumRule {
  // When present, the rule only applies on sheets whose name matches.
  sheet?: RegExp;
  header: RegExp;
  options: string[];
}

// Sheet-specific rules first — the first matching rule wins.
const HEADER_ENUM_RULES: HeaderEnumRule[] = [
  {
    sheet: /^stage ?3$/i,
    header: /^shareholder type$/i,
    options: STAGE3_SHAREHOLDER_TYPES,
  },
  {
    sheet: /^shareholders$/i,
    header: /^shareholder type$/i,
    options: STAGE4_SHAREHOLDER_TYPES,
  },
  { sheet: /^securities$/i, header: /^type$/i, options: SECURITY_TYPES },
  {
    sheet: /^split ?consolidation$/i,
    header: /^type$/i,
    options: ["Split", "Reverse_Split"],
  },
  {
    sheet: /^split ?consolidation$/i,
    header: /^allow update conversion ratio$/i,
    options: YES_NO,
  },
  {
    sheet: /^split ?consolidation$/i,
    header: /^rounding method$/i,
    options: ["RoundDown", "RoundOff", "RoundUp"],
  },
  { header: /^security type$/i, options: SECURITY_TYPES },
  { header: /^issuance type$/i, options: ISSUANCE_TYPES },
  { header: /^non-?priced$/i, options: YES_NO },
  { header: /currency$/i, options: CURRENCIES },
];

// The stage-4 "Get Started" sheet has no header row — its dropdown cells are
// fixed addresses (0-based row/col): the Yes/No questionnaire and the currency.
const GET_STARTED_SHEET = /^get ?started$/i;
const getStartedCellEnum = (row: number, col: number): string[] | null => {
  if (row === 10 && col === 4) return CURRENCIES; // E11
  if (row >= 15 && row <= 19 && (col === 1 || col === 7)) return YES_NO; // B16:B20, H16:H20
  if (row === 23 && col === 1) return YES_NO; // B24
  return null;
};

// How many leading rows are scanned for a matching column header.
const HEADER_SCAN_ROWS = 3;

export const ExcelViewerModal: React.FC<Props> = ({
  url,
  title,
  onClose,
  runId,
  cin,
  target,
  onSaved,
}) => {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [originalSheets, setOriginalSheets] = useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // "edit mode" = the user clicked the Edit button and the grid accepts changes
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Which cell is focused (blue selection ring) — also the "active" cell of a
  // multi-cell selection (the end the keyboard moves).
  const [selectedCell, setSelectedCell] = useState<CellCoord | null>(null);
  // The opposite corner of a range selection. Range spans anchor↔selectedCell.
  const [selectionAnchor, setSelectionAnchor] = useState<CellCoord | null>(
    null,
  );
  // True while the mouse is held down dragging out a selection.
  const isSelectingRef = useRef(false);
  // Which cell is actively being typed into (shows an <input>)
  const [editingCell, setEditingCell] = useState<CellCoord | null>(null);
  const [draftValue, setDraftValue] = useState("");

  // Select a single cell (collapses any range to that one cell).
  const selectCell = useCallback((cell: CellCoord | null) => {
    setSelectedCell(cell);
    setSelectionAnchor(cell);
  }, []);

  // View controls
  const [wrapText, setWrapText] = useState(false);
  const [freezeHeader, setFreezeHeader] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [formulaExpanded, setFormulaExpanded] = useState(false);

  // In-sheet search
  const [searchQuery, setSearchQuery] = useState("");
  const [matchIdx, setMatchIdx] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const gridRef = useRef<HTMLDivElement>(null);
  const cellInputRef = useRef<HTMLInputElement>(null);

  // Column widths (pixels), keyed by sheet index
  const [colWidths, setColWidths] = useState<Record<number, number[]>>({});
  const resizingRef = useRef<{
    sheetIdx: number;
    colIdx: number;
    startX: number;
    startWidth: number;
  } | null>(null);

  const getColWidth = useCallback(
    (ci: number) => colWidths[activeSheet]?.[ci] ?? DEFAULT_COL_WIDTH,
    [colWidths, activeSheet],
  );

  const startColResize = useCallback(
    (e: React.MouseEvent, ci: number) => {
      e.preventDefault();
      e.stopPropagation();
      resizingRef.current = {
        sheetIdx: activeSheet,
        colIdx: ci,
        startX: e.clientX,
        startWidth: colWidths[activeSheet]?.[ci] ?? DEFAULT_COL_WIDTH,
      };

      const onMouseMove = (ev: MouseEvent) => {
        if (!resizingRef.current) return;
        const { sheetIdx, colIdx, startX, startWidth } = resizingRef.current;
        const newWidth = Math.max(
          MIN_COL_WIDTH,
          startWidth + ev.clientX - startX,
        );
        setColWidths((prev) => {
          const sheetWidths = prev[sheetIdx] ? [...prev[sheetIdx]] : [];
          sheetWidths[colIdx] = newWidth;
          return { ...prev, [sheetIdx]: sheetWidths };
        });
      };

      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";

      const onMouseUp = () => {
        resizingRef.current = null;
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [activeSheet, colWidths],
  );

  const { mutateAsync: uploadExcel, isLoading: saving } =
    useUploadCaptableExcel();

  const canEdit = !!runId && !!cin;

  // Upload from disk
  const diskInputRef = useRef<HTMLInputElement>(null);
  const [diskUploading, setDiskUploading] = useState(false);
  const [diskUploadResult, setDiskUploadResult] = useState<
    "success" | "error" | null
  >(null);
  const [diskUploadError, setDiskUploadError] = useState<string | null>(null);

  const handleDiskUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !runId || !cin) return;
    setDiskUploading(true);
    setDiskUploadResult(null);
    setDiskUploadError(null);
    try {
      await uploadExcel({ runId, cin, file, fileName: file.name, target });
      setDiskUploadResult("success");
      onSaved?.();
    } catch (err: any) {
      setDiskUploadError(
        err?.response?.data?.message ?? err?.message ?? "Upload failed.",
      );
      setDiskUploadResult("error");
    } finally {
      setDiskUploading(false);
    }
  };

  // ─── Load workbook ────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      .then((buf) => {
        if (cancelled) return;
        const wb = XLSX.read(buf, { type: "array" });
        const parsed: SheetData[] = wb.SheetNames.map((name) => ({
          name,
          rows: trimTrailingEmptyRows(
            XLSX.utils.sheet_to_json(wb.Sheets[name], {
              header: 1,
              defval: "",
            }) as string[][],
          ),
        }));
        setSheets(parsed);
        setOriginalSheets(
          parsed.map((s) => ({ ...s, rows: s.rows.map((r) => [...r]) })),
        );
        // Auto-fit every sheet's columns to its content on first load.
        const widths: Record<number, number[]> = {};
        parsed.forEach((s, i) => {
          widths[i] = computeAutoFitWidths(s.rows);
        });
        setColWidths(widths);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  // ─── Derived sheet data ───────────────────────────────────────────────────

  const current = sheets[activeSheet];

  // Crop trailing empty columns so the grid stops at the last column with data
  // rather than padding out to the sheet's declared width.
  const columnCount = useMemo(() => {
    if (!current) return 0;
    let last = -1;
    current.rows.forEach((r) => {
      for (let c = r.length - 1; c >= 0; c -= 1) {
        if (String(r[c] ?? "").trim() !== "") {
          if (c > last) last = c;
          break;
        }
      }
    });
    // Always show at least one column so an empty sheet still renders a grid.
    return Math.max(last + 1, 1);
  }, [current]);

  const rowCount = current?.rows.length ?? 0;

  // Evaluate the sheet's formulas. `display` is what each cell renders (computed
  // result for "=" cells, raw text otherwise); `numbers` is the numeric value of
  // each cell (or null) and drives the status-bar aggregates.
  const evaluated = useMemo(
    () => (current ? evaluateGrid(sheets, activeSheet, columnCount) : null),
    [sheets, activeSheet, current, columnCount],
  );

  // Displayed (computed) text of a cell; falls back to raw before evaluation.
  const displayAt = useCallback(
    (r: number, c: number): string =>
      evaluated?.display[r]?.[c] ?? String(current?.rows[r]?.[c] ?? ""),
    [evaluated, current],
  );

  // ─── Enum dropdown resolution (edit mode) ─────────────────────────────────

  // Column → template enum for the active sheet, resolved by matching the
  // leading rows against the header rules. First matching rule wins.
  const enumByCol = useMemo(() => {
    const map = new Map<number, { options: string[]; headerRow: number }>();
    if (!current) return map;
    const sheetName = current.name;
    const scanRows = Math.min(HEADER_SCAN_ROWS, current.rows.length);
    for (let c = 0; c < columnCount; c += 1) {
      for (let r = 0; r < scanRows && !map.has(c); r += 1) {
        const text = String(current.rows[r]?.[c] ?? "").trim();
        if (text) {
          const rule = HEADER_ENUM_RULES.find(
            (candidate) =>
              (!candidate.sheet || candidate.sheet.test(sheetName)) &&
              candidate.header.test(text),
          );
          if (rule) map.set(c, { options: rule.options, headerRow: r });
        }
      }
    }
    return map;
  }, [current, columnCount]);

  // Enum options for one cell, or null when the cell is free text.
  const cellEnumOptions = useCallback(
    (row: number, col: number): string[] | null => {
      if (!current) return null;
      if (GET_STARTED_SHEET.test(current.name)) {
        return getStartedCellEnum(row, col);
      }
      const colEnum = enumByCol.get(col);
      return colEnum && row > colEnum.headerRow ? colEnum.options : null;
    },
    [current, enumByCol],
  );

  // Options for the cell being edited, filtered by what's typed. The full list
  // shows while the draft still equals the committed value (opening a filled
  // cell means "pick a different value"), and as a fallback when nothing
  // matches so the menu never dead-ends.
  const editingEnumOptions = useMemo(() => {
    if (!editingCell) return null;
    const options = cellEnumOptions(editingCell.row, editingCell.col);
    if (!options) return null;
    const committed = String(
      current?.rows[editingCell.row]?.[editingCell.col] ?? "",
    );
    if (draftValue === committed) return options;
    const query = draftValue.trim().toLowerCase();
    if (!query) return options;
    const filtered = options.filter((o) => o.toLowerCase().includes(query));
    return filtered.length ? filtered : options;
  }, [editingCell, cellEnumOptions, draftValue, current]);

  // Keyboard highlight within the open dropdown; -1 = nothing highlighted yet,
  // so plain Enter still commits free text.
  const [enumHighlight, setEnumHighlight] = useState(-1);
  const enumListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEnumHighlight(-1);
  }, [editingCell, draftValue]);

  useEffect(() => {
    if (enumHighlight < 0) return;
    enumListRef.current
      ?.querySelector(`[data-idx="${enumHighlight}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [enumHighlight]);

  // ─── Selection range + status-bar aggregates ──────────────────────────────

  // Normalised rectangle spanning the anchor and the active (selected) cell.
  const selectionRange = useMemo(() => {
    if (!selectedCell) return null;
    const anchor = selectionAnchor ?? selectedCell;
    return {
      minRow: Math.min(anchor.row, selectedCell.row),
      maxRow: Math.max(anchor.row, selectedCell.row),
      minCol: Math.min(anchor.col, selectedCell.col),
      maxCol: Math.max(anchor.col, selectedCell.col),
    };
  }, [selectedCell, selectionAnchor]);

  const isInSelection = useCallback(
    (r: number, c: number) =>
      !!selectionRange &&
      r >= selectionRange.minRow &&
      r <= selectionRange.maxRow &&
      c >= selectionRange.minCol &&
      c <= selectionRange.maxCol,
    [selectionRange],
  );

  // Excel-style aggregates over the selected block. Only meaningful when more
  // than one cell is selected; for a single cell we show nothing.
  const selectionStats = useMemo(() => {
    if (!selectionRange || !evaluated) return null;
    const { minRow, maxRow, minCol, maxCol } = selectionRange;
    const cellTotal = (maxRow - minRow + 1) * (maxCol - minCol + 1);
    if (cellTotal <= 1) return null;
    let count = 0; // non-empty cells
    let numCount = 0;
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    for (let r = minRow; r <= maxRow; r += 1) {
      for (let c = minCol; c <= maxCol; c += 1) {
        // Aggregate over computed values so formula results are included.
        if ((evaluated.display[r]?.[c] ?? "").trim() !== "") count += 1;
        const n = evaluated.numbers[r]?.[c];
        if (n != null && Number.isFinite(n)) {
          numCount += 1;
          sum += n;
          if (n < min) min = n;
          if (n > max) max = n;
        }
      }
    }
    return {
      cellTotal,
      count,
      numCount,
      sum,
      average: numCount ? sum / numCount : 0,
      min: numCount ? min : 0,
      max: numCount ? max : 0,
    };
  }, [selectionRange, evaluated]);

  const fmtNum = useCallback(
    (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 }),
    [],
  );

  // Total pixel width of the row-number gutter + all data columns. Giving the
  // table an explicit width (rather than letting `table-layout: fixed` collapse
  // it to the container) is what lets the grid scroll horizontally.
  const ROW_HEADER_W = 48;
  const tableWidth = useMemo(() => {
    let w = ROW_HEADER_W;
    for (let c = 0; c < columnCount; c += 1) w += getColWidth(c);
    return w;
  }, [columnCount, getColWidth]);

  const getCellValue = useCallback(
    (row: number, col: number): string =>
      String(current?.rows[row]?.[col] ?? ""),
    [current],
  );

  const setCellValue = useCallback(
    (row: number, col: number, value: string) => {
      setSheets((prev) =>
        prev.map((sheet, si) => {
          if (si !== activeSheet) return sheet;
          const rows = sheet.rows.map((r) => [...r]);
          const r = rows[row] ?? [];
          while (r.length <= col) r.push("");
          r[col] = value;
          rows[row] = r;
          return { ...sheet, rows };
        }),
      );
      setDirty(true);
      setSaved(false);
    },
    [activeSheet],
  );

  // ─── Search ───────────────────────────────────────────────────────────────

  const matches = useMemo<CellCoord[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || !current) return [];
    const out: CellCoord[] = [];
    current.rows.forEach((_row, r) => {
      for (let c = 0; c < columnCount; c += 1) {
        // Match against the displayed (computed) value, like Excel's Find.
        if (displayAt(r, c).toLowerCase().includes(q)) {
          out.push({ row: r, col: c });
        }
      }
    });
    return out;
  }, [searchQuery, current, columnCount, displayAt]);

  const matchKeys = useMemo(
    () => new Set(matches.map((m) => `${m.row}-${m.col}`)),
    [matches],
  );
  const currentMatch = matches[matchIdx] ?? null;

  const gotoMatch = useCallback(
    (dir: number) => {
      if (!matches.length) return;
      setMatchIdx((i) => (i + dir + matches.length) % matches.length);
    },
    [matches.length],
  );

  // Reset to the first hit whenever the query (or sheet) changes.
  useEffect(() => {
    setMatchIdx(0);
  }, [searchQuery, activeSheet]);

  // Select and scroll the active match into view.
  useEffect(() => {
    if (!matches.length) return;
    const m = matches[matchIdx];
    if (!m) return;
    selectCell(m);
    const el = gridRef.current?.querySelector<HTMLElement>(
      `[data-cell="${m.row}-${m.col}"]`,
    );
    el?.scrollIntoView({ block: "center", inline: "center" });
  }, [matchIdx, matches]);

  // Ctrl/Cmd+F focuses the in-sheet search box.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // End drag-selection wherever the mouse is released.
  useEffect(() => {
    const onUp = () => {
      isSelectingRef.current = false;
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);

  // ─── Focus management ────────────────────────────────────────────────────

  useEffect(() => {
    if (editingCell) {
      requestAnimationFrame(() => cellInputRef.current?.focus());
    } else {
      requestAnimationFrame(() => gridRef.current?.focus());
    }
  }, [editingCell]);

  // ─── Edit-mode lifecycle ──────────────────────────────────────────────────

  // When leaving edit mode (save or discard), clear cell state.
  useEffect(() => {
    if (!editMode) {
      setEditingCell(null);
      setSelectedCell(null);
      setSelectionAnchor(null);
    }
  }, [editMode]);

  // ─── Cell interaction helpers ─────────────────────────────────────────────

  const clamp = useCallback(
    (row: number, col: number): CellCoord => ({
      row: Math.max(0, Math.min(row, rowCount - 1)),
      col: Math.max(0, Math.min(col, columnCount - 1)),
    }),
    [rowCount, columnCount],
  );

  const commitEdit = useCallback(
    (next?: CellCoord) => {
      if (!editingCell) return;
      setCellValue(editingCell.row, editingCell.col, draftValue);
      setEditingCell(null);
      selectCell(next ?? editingCell);
    },
    [editingCell, draftValue, setCellValue, selectCell],
  );

  const startEditing = useCallback(
    (row: number, col: number, initial?: string) => {
      if (!editMode) return;
      selectCell({ row, col });
      setEditingCell({ row, col });
      setDraftValue(initial ?? getCellValue(row, col));
    },
    [editMode, getCellValue, selectCell],
  );

  // Write a dropdown option into the editing cell and close the editor
  // (selection stays on the cell, matching Excel's dropdown behaviour).
  const commitEnumOption = useCallback(
    (option: string) => {
      if (!editingCell) return;
      setCellValue(editingCell.row, editingCell.col, option);
      setEditingCell(null);
      selectCell(editingCell);
    },
    [editingCell, setCellValue, selectCell],
  );

  // ─── Keyboard: grid (no cell editing active) ─────────────────────────────

  // Copy the current selection (whole range) to the clipboard as TSV so it
  // pastes cleanly into Excel/Sheets.
  const copySelection = useCallback(() => {
    if (!selectionRange) return;
    const { minRow, maxRow, minCol, maxCol } = selectionRange;
    const lines: string[] = [];
    for (let r = minRow; r <= maxRow; r += 1) {
      const cells: string[] = [];
      for (let c = minCol; c <= maxCol; c += 1) {
        // Copy computed values so pasted data isn't raw "=…" formulas.
        cells.push(displayAt(r, c));
      }
      lines.push(cells.join("\t"));
    }
    navigator.clipboard?.writeText(lines.join("\n")).catch(() => {});
  }, [selectionRange, displayAt]);

  // Clear every cell in the current selection (used by Delete and Cut). Batched
  // into a single state update so large ranges don't re-render per cell.
  const clearSelectionCells = useCallback(() => {
    if (!selectionRange) return;
    const { minRow, maxRow, minCol, maxCol } = selectionRange;
    setSheets((prev) =>
      prev.map((sheet, si) => {
        if (si !== activeSheet) return sheet;
        const rows = sheet.rows.map((r) => [...r]);
        for (let r = minRow; r <= maxRow; r += 1) {
          const row = rows[r];
          if (row) {
            for (let c = minCol; c <= maxCol; c += 1) {
              if (c < row.length) row[c] = "";
            }
          }
        }
        return { ...sheet, rows };
      }),
    );
    setDirty(true);
    setSaved(false);
  }, [selectionRange, activeSheet]);

  // Paste tab/newline-separated clipboard text into the grid, anchored at the
  // top-left of the current selection. Grows the sheet as needed.
  const pasteFromClipboard = useCallback(async () => {
    if (!editMode || !selectedCell) return;
    let text = "";
    try {
      text = await navigator.clipboard.readText();
    } catch {
      return;
    }
    if (!text) return;
    const lines = text.replace(/\r\n?/g, "\n").split("\n");
    if (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
    const startR = selectionRange?.minRow ?? selectedCell.row;
    const startC = selectionRange?.minCol ?? selectedCell.col;
    setSheets((prev) =>
      prev.map((sheet, si) => {
        if (si !== activeSheet) return sheet;
        const rows = sheet.rows.map((r) => [...r]);
        lines.forEach((line, dr) => {
          const rr = startR + dr;
          while (rows.length <= rr) rows.push([]);
          const row = rows[rr];
          line.split("\t").forEach((val, dc) => {
            const cc = startC + dc;
            while (row.length <= cc) row.push("");
            row[cc] = val;
          });
        });
        return { ...sheet, rows };
      }),
    );
    setDirty(true);
    setSaved(false);
  }, [editMode, selectedCell, selectionRange, activeSheet]);

  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (editingCell || !selectedCell) return;
      const { row, col } = selectedCell;
      // Shift+arrow extends the range (moves the active cell, keeps the anchor).
      // Plain arrow collapses to a single cell.
      const move = (r: number, c: number) => {
        const next = clamp(r, c);
        if (e.shiftKey) setSelectedCell(next);
        else selectCell(next);
      };
      // Clipboard / select-all shortcuts (Ctrl on Win/Linux, Cmd on macOS).
      if (e.ctrlKey || e.metaKey) {
        const k = e.key.toLowerCase();
        if (k === "c") {
          e.preventDefault();
          copySelection();
          return;
        }
        if (k === "v") {
          e.preventDefault();
          pasteFromClipboard();
          return;
        }
        if (k === "x") {
          e.preventDefault();
          copySelection();
          if (editMode) clearSelectionCells();
          return;
        }
        if (k === "a") {
          e.preventDefault();
          setSelectionAnchor({ row: 0, col: 0 });
          setSelectedCell({
            row: Math.max(0, rowCount - 1),
            col: Math.max(0, columnCount - 1),
          });
          return;
        }
      }
      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          move(row - 1, col);
          break;
        case "ArrowDown":
          e.preventDefault();
          move(row + 1, col);
          break;
        case "ArrowLeft":
          e.preventDefault();
          move(row, col - 1);
          break;
        case "ArrowRight":
          e.preventDefault();
          move(row, col + 1);
          break;
        case "Tab":
          e.preventDefault();
          selectCell(
            col < columnCount - 1 ? clamp(row, col + 1) : clamp(row + 1, 0),
          );
          break;
        case "Enter":
        case "F2":
          e.preventDefault();
          if (editMode) startEditing(row, col);
          break;
        case "Escape":
          selectCell(null);
          break;
        case "Delete":
        case "Backspace":
          if (editMode && selectionRange) {
            e.preventDefault();
            clearSelectionCells();
          }
          break;
        default:
          if (editMode && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            startEditing(row, col, e.key);
          }
      }
    },
    [
      editingCell,
      selectedCell,
      editMode,
      clamp,
      columnCount,
      rowCount,
      startEditing,
      selectCell,
      selectionRange,
      copySelection,
      pasteFromClipboard,
      clearSelectionCells,
    ],
  );

  // ─── Keyboard: cell input ─────────────────────────────────────────────────

  const handleCellInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!editingCell) return;
      const { row, col } = editingCell;
      // Dropdown navigation takes precedence while the enum menu is open.
      if (editingEnumOptions?.length) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setEnumHighlight((h) =>
            Math.min(h + 1, editingEnumOptions.length - 1),
          );
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setEnumHighlight((h) => Math.max(h - 1, 0));
          return;
        }
        if (e.key === "Enter" && enumHighlight >= 0) {
          e.preventDefault();
          commitEnumOption(editingEnumOptions[enumHighlight]);
          return;
        }
      }
      switch (e.key) {
        case "Enter":
          e.preventDefault();
          commitEdit(clamp(row + 1, col));
          break;
        case "Tab":
          e.preventDefault();
          commitEdit(
            col < columnCount - 1 ? clamp(row, col + 1) : clamp(row + 1, 0),
          );
          break;
        case "Escape":
          e.preventDefault();
          setEditingCell(null);
          selectCell(editingCell);
          break;
        default:
          break;
      }
    },
    [
      editingCell,
      editingEnumOptions,
      enumHighlight,
      commitEnumOption,
      commitEdit,
      clamp,
      columnCount,
      selectCell,
    ],
  );

  // ─── Save / discard ───────────────────────────────────────────────────────

  const buildBlob = (): Blob => {
    const wb = XLSX.utils.book_new();
    sheets.forEach((sheet) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(sheet.rows),
        sheet.name,
      );
    });
    const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    return new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  };

  const handleSave = async () => {
    if (!runId || !cin) return;
    setSaveError(null);
    setSaved(false);
    try {
      await uploadExcel({
        runId,
        cin,
        file: buildBlob(),
        fileName:
          target === "stage4" ? `${cin}-stage4.xlsx` : `${cin}-captable.xlsx`,
        target,
      });
      setDirty(false);
      setEditMode(false);
      setSaved(true);
      onSaved?.();
    } catch (err: any) {
      setSaveError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to upload edited captable.",
      );
    }
  };

  const requestClose = () => {
    // eslint-disable-next-line no-alert -- guard against silently losing edits
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    onClose();
  };

  const handleDiscard = () => {
    if (
      dirty &&
      // eslint-disable-next-line no-alert -- guard against silently losing edits
      !window.confirm("Discard unsaved changes?")
    )
      return;
    setSheets(
      originalSheets.map((s) => ({ ...s, rows: s.rows.map((r) => [...r]) })),
    );
    setDirty(false);
    setEditMode(false);
    setSaveError(null);
  };

  // Re-fit the active sheet's columns to their content.
  const handleAutoFit = useCallback(() => {
    if (!current) return;
    setColWidths((prev) => ({
      ...prev,
      [activeSheet]: computeAutoFitWidths(current.rows),
    }));
  }, [current, activeSheet]);

  // ─── Formula bar values ───────────────────────────────────────────────────

  const cellRefLabel =
    selectedCell !== null
      ? `${colLabel(selectedCell.col)}${selectedCell.row + 1}`
      : "";

  const formulaBarValue = editingCell
    ? draftValue
    : selectedCell !== null
      ? getCellValue(selectedCell.row, selectedCell.col)
      : "";

  const formulaReadOnly = !editMode || !editingCell;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        className="rounded-xl shadow-2xl flex flex-col w-[95vw] h-[90vh] overflow-hidden"
        style={{ fontFamily: "'Calibri', 'Segoe UI', Arial, sans-serif" }}
      >
        {/* ── Title bar (Excel green) ───────────────────────────────────── */}
        <div className="flex items-center justify-between px-4 py-2 shrink-0 bg-[#217346]">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-bold bg-white text-[#217346] rounded px-1 shrink-0 select-none">
              XLS
            </span>
            <span
              className="text-sm font-medium text-white truncate"
              title={title}
            >
              {title}
            </span>
            {dirty && (
              <span className="text-xs text-green-200 shrink-0">· Unsaved</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 ml-4 shrink-0">
            {canEdit && !editMode && (
              <button
                onClick={() => {
                  setEditMode(true);
                  setSaved(false);
                }}
                disabled={loading || !!error}
                className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-white text-[#217346] rounded hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
            )}
            {canEdit && editMode && (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-white text-[#217346] rounded hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Save className="w-3 h-3" />
                  )}
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={handleDiscard}
                  disabled={saving}
                  className="flex items-center gap-1 px-3 py-1 text-xs text-white border border-white/40 rounded hover:bg-white/20 disabled:opacity-40"
                >
                  Discard
                </button>
              </>
            )}
            {canEdit && (
              <>
                <input
                  ref={diskInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleDiskUpload}
                />
                <button
                  onClick={() => diskInputRef.current?.click()}
                  disabled={loading || !!error || diskUploading || saving}
                  title="Upload a locally-edited Excel file to replace this result"
                  className="flex items-center gap-1 px-3 py-1 text-xs text-white border border-white/40 rounded hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {diskUploading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Upload className="w-3 h-3" />
                  )}
                  {diskUploading ? "Uploading…" : "Upload corrected"}
                </button>
              </>
            )}
            <a
              href={url}
              download
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 px-3 py-1 text-xs text-white border border-white/40 rounded hover:bg-white/20"
            >
              <Download className="w-3 h-3" />
              Download
            </a>
            <button
              onClick={requestClose}
              className="p-1 rounded hover:bg-white/20 text-white ml-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Save / error banners ──────────────────────────────────────── */}
        {saved && (
          <div className="flex items-center gap-1.5 px-4 py-1.5 text-xs text-green-700 bg-green-50 border-b border-green-200 shrink-0">
            <Check className="w-3.5 h-3.5" />
            Captable saved successfully.
          </div>
        )}
        {saveError && (
          <div className="px-4 py-1.5 text-xs text-red-600 bg-red-50 border-b border-red-200 shrink-0">
            {saveError}
          </div>
        )}
        {diskUploadResult === "success" && (
          <div className="flex items-center gap-1.5 px-4 py-1.5 text-xs text-green-700 bg-green-50 border-b border-green-200 shrink-0">
            <Check className="w-3.5 h-3.5" />
            File uploaded successfully.
          </div>
        )}
        {diskUploadResult === "error" && (
          <div className="px-4 py-1.5 text-xs text-red-600 bg-red-50 border-b border-red-200 shrink-0">
            {diskUploadError}
          </div>
        )}

        {/* ── Quick toolbar (search · view controls · zoom) ─────────────── */}
        {!loading && !error && current && (
          <div
            className="flex items-center gap-1 px-2 py-1 border-b bg-[#f8f9fa] shrink-0 text-gray-600"
            style={{ fontSize: 12 }}
          >
            {/* Search */}
            <div
              className="flex items-center gap-1 rounded border bg-white px-1.5"
              style={{ height: 26 }}
            >
              <Search className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    gotoMatch(e.shiftKey ? -1 : 1);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setSearchQuery("");
                  }
                }}
                placeholder="Search this sheet…"
                className="outline-none bg-transparent text-gray-900"
                style={{ width: 150, fontSize: 12 }}
              />
              {searchQuery && (
                <>
                  <span
                    className="text-gray-400 select-none tabular-nums shrink-0"
                    style={{ fontSize: 11 }}
                  >
                    {matches.length ? `${matchIdx + 1}/${matches.length}` : "0"}
                  </span>
                  <button
                    onClick={() => gotoMatch(-1)}
                    disabled={!matches.length}
                    title="Previous match (Shift+Enter)"
                    className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => gotoMatch(1)}
                    disabled={!matches.length}
                    title="Next match (Enter)"
                    className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setSearchQuery("")}
                    title="Clear search"
                    className="p-0.5 rounded hover:bg-gray-100"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>

            <div className="w-px h-4 bg-gray-300 mx-1" />

            {/* View toggles */}
            <button
              onClick={() => setWrapText((w) => !w)}
              title="Toggle text wrapping"
              className={`flex items-center gap-1 px-2 rounded hover:bg-gray-200 ${
                wrapText ? "bg-gray-200 text-[#217346] font-medium" : ""
              }`}
              style={{ height: 26 }}
            >
              <WrapText className="w-3.5 h-3.5" />
              Wrap
            </button>
            <button
              onClick={handleAutoFit}
              title="Auto-fit column widths to content"
              className="flex items-center gap-1 px-2 rounded hover:bg-gray-200"
              style={{ height: 26 }}
            >
              <Maximize2 className="w-3.5 h-3.5" />
              Auto-fit
            </button>
            <button
              onClick={() => setFreezeHeader((f) => !f)}
              title="Freeze the header row while scrolling"
              className={`flex items-center gap-1 px-2 rounded hover:bg-gray-200 ${
                freezeHeader ? "bg-gray-200 text-[#217346] font-medium" : ""
              }`}
              style={{ height: 26 }}
            >
              <Pin className="w-3.5 h-3.5" />
              Freeze
            </button>

            <div className="w-px h-4 bg-gray-300 mx-1" />

            {/* Zoom */}
            <button
              onClick={() =>
                setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))
              }
              title="Zoom out"
              className="p-1 rounded hover:bg-gray-200"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setZoom(1)}
              title="Reset zoom"
              className="text-center tabular-nums hover:bg-gray-200 rounded select-none"
              style={{ width: 42, fontSize: 11, height: 26 }}
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              onClick={() =>
                setZoom((z) => Math.min(2, Math.round((z + 0.1) * 10) / 10))
              }
              title="Zoom in"
              className="p-1 rounded hover:bg-gray-200"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>

            <div className="flex-1" />

            {!editMode && (
              <span
                className="flex items-center gap-1 text-gray-400 select-none shrink-0"
                style={{ fontSize: 11 }}
                title={
                  canEdit
                    ? "Read-only — click Edit to make changes"
                    : "Read-only"
                }
              >
                <Lock className="w-3 h-3" />
                Read-only
              </span>
            )}
          </div>
        )}

        {/* ── Formula bar ───────────────────────────────────────────────── */}
        <div
          className="flex items-stretch border-b bg-white shrink-0"
          style={{ minHeight: 26 }}
        >
          {/* Cell reference box */}
          <div
            className="flex items-center justify-center border-r text-xs font-medium text-gray-600 bg-[#f2f2f2] shrink-0 select-none"
            style={{ width: 70, fontSize: 12 }}
          >
            {cellRefLabel}
          </div>
          {/* fx label */}
          <span
            className="flex items-center text-gray-400 px-2 italic shrink-0 select-none"
            style={{ fontSize: 12 }}
          >
            fx
          </span>
          {/* Value field — expands to a textarea when toggled */}
          {formulaExpanded ? (
            <textarea
              readOnly={formulaReadOnly}
              value={formulaBarValue}
              onChange={(e) => {
                if (editingCell) setDraftValue(e.target.value);
              }}
              className="flex-1 px-1 py-1 text-xs outline-none bg-transparent text-gray-900 resize-none"
              style={{ height: 80, fontSize: 12, fontFamily: "inherit" }}
            />
          ) : (
            <input
              readOnly={formulaReadOnly}
              value={formulaBarValue}
              onChange={(e) => {
                if (editingCell) setDraftValue(e.target.value);
              }}
              className="flex-1 px-1 text-xs outline-none bg-transparent text-gray-900"
              style={{ height: 26, fontSize: 12 }}
            />
          )}
          {/* Expand / collapse the formula bar */}
          <button
            onClick={() => setFormulaExpanded((v) => !v)}
            title={
              formulaExpanded ? "Collapse formula bar" : "Expand formula bar"
            }
            className="flex items-center justify-center border-l px-1.5 text-gray-500 hover:bg-gray-100 shrink-0"
          >
            {formulaExpanded ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>
        </div>

        {/* ── Spreadsheet body ──────────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-h-0 bg-white">
          {/* Scrollable grid */}
          <div className="flex-1 overflow-auto min-h-0">
            {loading && (
              <div className="flex items-center justify-center h-full gap-2 text-gray-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading spreadsheet…
              </div>
            )}
            {error && (
              <div className="flex items-center justify-center h-full text-red-500 text-sm">
                Failed to load: {error}
              </div>
            )}
            {!loading && !error && current && (
              <div
                ref={gridRef}
                tabIndex={0}
                className="outline-none"
                onKeyDown={handleGridKeyDown}
              >
                <table
                  className="border-collapse"
                  style={{
                    tableLayout: "fixed",
                    width: tableWidth,
                    minWidth: "100%",
                    zoom,
                  }}
                >
                  {/* Column letter headers */}
                  <thead className="sticky top-0 z-20">
                    <tr>
                      {/* Corner */}
                      <th
                        className="sticky left-0 z-30 select-none"
                        style={{
                          width: 48,
                          minWidth: 48,
                          height: HEADER_H,
                          background: "#f2f2f2",
                          border: "1px solid #D0D0D0",
                        }}
                      />
                      {Array.from({ length: columnCount }).map((_, ci) => (
                        <th
                          key={ci}
                          className="select-none text-center font-normal"
                          onMouseDown={(e) => {
                            if (e.button !== 0) return;
                            // Click a column letter → select the whole column.
                            selectCell({ row: 0, col: ci });
                            setSelectedCell({
                              row: Math.max(0, rowCount - 1),
                              col: ci,
                            });
                            gridRef.current?.focus();
                          }}
                          style={{
                            width: getColWidth(ci),
                            minWidth: MIN_COL_WIDTH,
                            height: HEADER_H,
                            boxSizing: "border-box",
                            background:
                              selectionRange &&
                              ci >= selectionRange.minCol &&
                              ci <= selectionRange.maxCol
                                ? "#c8e4b2"
                                : "#f2f2f2",
                            border: "1px solid #D0D0D0",
                            color: "#595959",
                            fontSize: 11,
                            padding: "2px 4px",
                            fontFamily: "inherit",
                            position: "relative",
                            cursor: "pointer",
                          }}
                        >
                          {colLabel(ci)}
                          {/* Resize handle */}
                          <span
                            onMouseDown={(e) => startColResize(e, ci)}
                            onDoubleClick={handleAutoFit}
                            style={{
                              position: "absolute",
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: 5,
                              cursor: "col-resize",
                              zIndex: 1,
                            }}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <colgroup>
                    <col style={{ width: 48, minWidth: 48 }} />
                    {Array.from({ length: columnCount }).map((_, ci) => (
                      <col
                        key={ci}
                        style={{
                          width: getColWidth(ci),
                          minWidth: MIN_COL_WIDTH,
                        }}
                      />
                    ))}
                  </colgroup>
                  <tbody>
                    {current.rows.map((_row, ri) => {
                      const isHeader = ri === 0;
                      const frozen = freezeHeader && ri === 0;
                      return (
                        <tr key={ri}>
                          {/* Row number */}
                          <td
                            className="select-none text-right"
                            onMouseDown={(e) => {
                              if (e.button !== 0) return;
                              // Click a row number → select the whole row.
                              selectCell({ row: ri, col: 0 });
                              setSelectedCell({
                                row: ri,
                                col: Math.max(0, columnCount - 1),
                              });
                              gridRef.current?.focus();
                            }}
                            style={{
                              width: 48,
                              minWidth: 48,
                              position: "sticky",
                              left: 0,
                              top: frozen ? HEADER_H : undefined,
                              zIndex: frozen ? 25 : 10,
                              cursor: "pointer",
                              background:
                                selectionRange &&
                                ri >= selectionRange.minRow &&
                                ri <= selectionRange.maxRow
                                  ? "#c8e4b2"
                                  : "#f2f2f2",
                              border: "1px solid #D0D0D0",
                              color: "#595959",
                              fontSize: 11,
                              padding: "0 6px 0 2px",
                              fontFamily: "inherit",
                              verticalAlign: wrapText ? "top" : "middle",
                            }}
                          >
                            {ri + 1}
                          </td>

                          {Array.from({ length: columnCount }).map((_, ci) => {
                            // Show the computed value; the raw formula stays in
                            // the formula bar / cell editor.
                            const cellVal = displayAt(ri, ci);
                            const isNumericCell =
                              (evaluated?.numbers[ri]?.[ci] ?? null) !== null;
                            const isSelected =
                              selectedCell?.row === ri &&
                              selectedCell?.col === ci;
                            const isEditing =
                              editingCell?.row === ri &&
                              editingCell?.col === ci;
                            const isMatch = matchKeys.has(`${ri}-${ci}`);
                            const isCurrentMatch =
                              currentMatch?.row === ri &&
                              currentMatch?.col === ci;
                            const inRange = isInSelection(ri, ci);

                            let background: string;
                            if (isCurrentMatch) background = "#ffca28";
                            else if (isMatch) background = "#fff59d";
                            // Tint the non-active cells of a multi-cell selection.
                            else if (inRange && !isSelected)
                              background = "#d6ebc7";
                            else if (isHeader) background = "#E9F0DB";
                            else background = ri % 2 === 0 ? "#fff" : "#fafafa";

                            return (
                              <td
                                key={ci}
                                data-cell={`${ri}-${ci}`}
                                onMouseDown={(e) => {
                                  if (isEditing) return;
                                  e.preventDefault(); // avoid native text drag-select
                                  if (editingCell) commitEdit();
                                  if (e.shiftKey && selectedCell) {
                                    // Extend the range to this cell, keep anchor.
                                    setSelectedCell({ row: ri, col: ci });
                                  } else {
                                    selectCell({ row: ri, col: ci });
                                    isSelectingRef.current = true;
                                  }
                                  gridRef.current?.focus();
                                }}
                                onMouseEnter={() => {
                                  if (isSelectingRef.current && !editingCell) {
                                    setSelectedCell({ row: ri, col: ci });
                                  }
                                }}
                                onDoubleClick={() => startEditing(ri, ci)}
                                style={{
                                  height: wrapText ? undefined : 22,
                                  verticalAlign: wrapText ? "top" : "middle",
                                  padding: isEditing
                                    ? 0
                                    : wrapText
                                      ? "3px 6px"
                                      : "0 6px",
                                  background,
                                  border: isSelected
                                    ? "2px solid #217346"
                                    : isEditing
                                      ? "2px solid #1565C0"
                                      : "1px solid #D0D0D0",
                                  fontWeight: isHeader ? 600 : 400,
                                  color: isHeader ? "#1F3864" : "#111",
                                  fontSize: 12,
                                  fontFamily: "inherit",
                                  // Excel right-aligns numbers (incl. formula
                                  // results); text stays left-aligned.
                                  textAlign:
                                    !isHeader && isNumericCell && !isEditing
                                      ? "right"
                                      : undefined,
                                  cursor: editMode ? "cell" : "default",
                                  overflow: isEditing ? "visible" : "hidden",
                                  whiteSpace: isEditing
                                    ? undefined
                                    : wrapText
                                      ? "pre-wrap"
                                      : "nowrap",
                                  wordBreak: wrapText
                                    ? "break-word"
                                    : undefined,
                                  textOverflow: wrapText
                                    ? undefined
                                    : "ellipsis",
                                  maxWidth: 0,
                                  position: frozen ? "sticky" : "relative",
                                  top: frozen ? HEADER_H : undefined,
                                  zIndex: frozen ? 15 : undefined,
                                  boxSizing: "border-box",
                                }}
                                // Tooltip shows the full text for truncated cells.
                                title={
                                  isEditing || wrapText ? undefined : cellVal
                                }
                              >
                                {isEditing ? (
                                  <>
                                    <input
                                      ref={cellInputRef}
                                      value={draftValue}
                                      onChange={(e) =>
                                        setDraftValue(e.target.value)
                                      }
                                      onKeyDown={handleCellInputKeyDown}
                                      onBlur={() => {
                                        // Commit when focus leaves the cell input
                                        // (clicking another cell or outside the grid).
                                        commitEdit();
                                      }}
                                      style={{
                                        display: "block",
                                        width: "100%",
                                        height: "100%",
                                        padding: "0 6px",
                                        border: "none",
                                        outline: "none",
                                        background: "white",
                                        fontSize: 12,
                                        fontFamily: "inherit",
                                        fontWeight: isHeader ? 600 : 400,
                                        color: isHeader ? "#1F3864" : "#111",
                                        boxSizing: "border-box",
                                      }}
                                    />
                                    {editingEnumOptions &&
                                      editingEnumOptions.length > 0 && (
                                        <div
                                          ref={enumListRef}
                                          style={{
                                            position: "absolute",
                                            top: "100%",
                                            left: -2,
                                            zIndex: 60,
                                            minWidth: "calc(100% + 4px)",
                                            maxHeight: 190,
                                            overflowY: "auto",
                                            background: "#fff",
                                            border: "1px solid #cbd5e1",
                                            borderRadius: 6,
                                            boxShadow:
                                              "0 8px 20px rgba(0,0,0,0.15)",
                                            padding: 2,
                                          }}
                                        >
                                          {editingEnumOptions.map((opt, i) => (
                                            <div
                                              key={opt}
                                              data-idx={i}
                                              // mousedown (not click) so the input's
                                              // blur-commit doesn't fire first.
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                commitEnumOption(opt);
                                              }}
                                              onMouseEnter={() =>
                                                setEnumHighlight(i)
                                              }
                                              style={{
                                                padding: "4px 8px",
                                                fontSize: 12,
                                                cursor: "pointer",
                                                borderRadius: 4,
                                                whiteSpace: "nowrap",
                                                background:
                                                  i === enumHighlight
                                                    ? "#DBEAFE"
                                                    : undefined,
                                              }}
                                            >
                                              {opt}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                  </>
                                ) : isSelected &&
                                  editMode &&
                                  cellEnumOptions(ri, ci) ? (
                                  // Selected enum cell: show a dropdown affordance.
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 4,
                                    }}
                                  >
                                    <span
                                      style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      {cellVal}
                                    </span>
                                    <ChevronDown
                                      className="w-3 h-3"
                                      style={{
                                        flexShrink: 0,
                                        color: "#217346",
                                        cursor: "pointer",
                                      }}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        startEditing(ri, ci);
                                      }}
                                    />
                                  </span>
                                ) : (
                                  cellVal
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Sheet tabs + status bar (bottom, Excel-style) ──────────── */}
          <div
            className="flex items-center border-t shrink-0"
            style={{ background: "#f2f2f2", height: 28 }}
          >
            {/* Tabs */}
            <div className="flex items-end overflow-x-auto flex-1 gap-px px-2">
              {sheets.map((s, i) => (
                <button
                  key={s.name}
                  onClick={() => {
                    if (editingCell) commitEdit();
                    setEditingCell(null);
                    selectCell(null);
                    setActiveSheet(i);
                  }}
                  style={{
                    background: i === activeSheet ? "white" : "#DCDCDC",
                    border: "1px solid #BFBFBF",
                    borderTop:
                      i === activeSheet
                        ? "2px solid #217346"
                        : "1px solid #BFBFBF",
                    borderBottom:
                      i === activeSheet
                        ? "1px solid white"
                        : "1px solid #BFBFBF",
                    padding: "1px 14px 2px",
                    fontSize: 11,
                    fontWeight: i === activeSheet ? 700 : 400,
                    color: i === activeSheet ? "#217346" : "#595959",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    marginBottom: i === activeSheet ? -1 : 0,
                    fontFamily: "inherit",
                  }}
                >
                  {s.name}
                </button>
              ))}
            </div>
            {/* Aggregates over the current selection (Excel-style) */}
            {selectionStats && (
              <div
                className="flex items-center gap-3 px-4 shrink-0 select-none text-gray-600 tabular-nums border-l"
                style={{ fontSize: 11 }}
              >
                {selectionStats.numCount > 0 && (
                  <>
                    <span title="Average of numeric cells">
                      Average:{" "}
                      <span className="font-semibold">
                        {fmtNum(selectionStats.average)}
                      </span>
                    </span>
                    <span title="Count of numeric cells">
                      Numerical Count:{" "}
                      <span className="font-semibold">
                        {selectionStats.numCount}
                      </span>
                    </span>
                    <span title="Minimum numeric value">
                      Min:{" "}
                      <span className="font-semibold">
                        {fmtNum(selectionStats.min)}
                      </span>
                    </span>
                    <span title="Maximum numeric value">
                      Max:{" "}
                      <span className="font-semibold">
                        {fmtNum(selectionStats.max)}
                      </span>
                    </span>
                    <span title="Sum of numeric cells">
                      Sum:{" "}
                      <span className="font-semibold">
                        {fmtNum(selectionStats.sum)}
                      </span>
                    </span>
                  </>
                )}
                <span title="Count of non-empty cells">
                  Count:{" "}
                  <span className="font-semibold">{selectionStats.count}</span>
                </span>
              </div>
            )}
            {/* Status bar */}
            <span
              className="text-gray-400 px-4 shrink-0 select-none"
              style={{ fontSize: 11 }}
            >
              {current
                ? `${current.rows.length} rows · ${columnCount} columns`
                : ""}
              {editMode && " · Edit Mode"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
